// Pure helpers of the Cockpit de direction (Ordre 23): rights, periods, how a figure reads, the change against the previous period, the
// trend series and the sections a person chose to fold. The server decides what exists; nothing here computes a business figure.
import { formatMinor } from "./finance";
import { hasPermission } from "./permissions";
import { readStored, writeStored } from "./apps";

const on = (user) => !!user?.features?.cockpit;
export const canOpenCockpit = (user) => on(user) && hasPermission(user, "analytics:view_company");
export const canExportCockpit = (user) => canOpenCockpit(user) && hasPermission(user, "analytics:export");

export const TREND_KPIS = ["rev.billed", "rev.collected", "com.leads", "sup.created", "sup.resolved"];
export const STATE_LABELS = { ok: "", partial: "Donnée partielle", no_data: "Aucune donnée", unavailable: "Indisponible", source_unavailable: "Source indisponible" };
export const SEVERITY_LABELS = { critical: "À traiter vite", warning: "À surveiller" };

// --- Periods ---------------------------------------------------------------------------------------------------------------------------
export const PRESETS = [["month", "Ce mois"], ["30d", "30 derniers jours"], ["quarter", "Ce trimestre"], ["year", "Cette année"], ["custom", "Période personnalisée"]];
const iso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export function presetRange(preset, today = new Date()) {
  const end = iso(today);
  if (preset === "30d") return { from: iso(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29)), to: end };
  if (preset === "quarter") return { from: iso(new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1)), to: end };
  if (preset === "year") return { from: iso(new Date(today.getFullYear(), 0, 1)), to: end };
  return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: end };
}

export function rangeProblem({ from, to }) {
  if (!from || !to) return "Choisissez un début et une fin.";
  if (from > to) return "Le début doit précéder la fin.";
  if ((new Date(to) - new Date(from)) / 86400000 >= 366) return "Une période d'un an au plus.";
  return null;
}

const dayText = (value) => (value ? value.split("-").reverse().join("/") : "");
export const rangeText = (from, to) => `${dayText(from)} → ${dayText(to)}`;

// What a tile says about WHEN its figure holds: only « period » indicators follow the filter (decision D-20).
export function whenText(kpi, period) {
  if (!kpi) return "";
  if (kpi.kind === "point") return "aujourd'hui";
  if (kpi.kind === "window") return `${kpi.window_days} derniers jours`;
  return period ? rangeText(period.from, period.to) : "sur la période";
}

// --- Reading a figure -------------------------------------------------------------------------------------------------------------------
const number = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(n);

export function formatScalar(value, unit) {
  if (value === null || value === undefined) return "—";
  if (unit === "rate") return `${number(value * 100)} %`;
  if (unit === "minutes") return value >= 120 ? `${Math.floor(value / 60)} h ${String(Math.round(value % 60)).padStart(2, "0")}` : `${number(value)} min`;
  if (unit === "hours") return `${number(value)} h`;
  if (unit === "days") return `${number(value)} j`;
  return number(value);
}

export const moneyLines = (money) => Object.entries(money || {}).sort(([a], [b]) => a.localeCompare(b)).map(([currency, minor]) => ({ currency, text: formatMinor(minor, currency) }));

export function partText(part, unit) {
  if (part.text) return part.text;
  if (part.money) return moneyLines(part.money).map((l) => l.text).join(" · ");
  if (part.value === null || part.value === undefined) return "—";
  // A share (0 to 1) is shown as a percentage; every other number as a count.
  return part.key === "share" || part.key === "first_response" || part.key === "resolution" ? `${number(part.value * 100)} %` : number(part.value);
}

// The change against the previous period (flows only — the server sends `previous` only where the source can compute it).
export function deltaOf(entry, unit) {
  if (entry.money && entry.previous_money) {
    const currencies = Object.keys(entry.money);
    const first = currencies[0];
    const before = entry.previous_money[first];
    if (!first || !before) return null;
    const change = Math.round(((entry.money[first] - before) / Math.abs(before)) * 100);
    return { text: `${change > 0 ? "+" : ""}${change} %${currencies.length > 1 ? ` (${first})` : ""}`, tone: change > 0 ? "up" : change < 0 ? "down" : "flat" };
  }
  if (typeof entry.value === "number" && typeof entry.previous === "number") {
    const diff = entry.value - entry.previous;
    if (unit === "rate") return null;
    return { text: `${diff > 0 ? "+" : ""}${number(diff)}`, tone: diff > 0 ? "up" : diff < 0 ? "down" : "flat" };
  }
  return null;
}

// --- Trends: one series per currency for money, one for a count ----------------------------------------------------------------------------
export function trendSeries(points, unit) {
  if (unit === "money") {
    const currencies = [...new Set(points.flatMap((p) => Object.keys(p.money || {})))].sort();
    return currencies.map((currency) => ({ key: currency, label: currency, unit: "money", currency, data: points.map((p) => ({ month: p.month, value: (p.money || {})[currency] ?? 0 })) }));
  }
  return [{ key: "count", label: "", unit, currency: null, data: points.map((p) => ({ month: p.month, value: p.value ?? 0 })) }];
}

export function monthLabel(month) {
  const [year, m] = month.split("-");
  return new Date(Number(year), Number(m) - 1, 1).toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
}

// --- Personal layout: folded sections, kept in this browser only (decision D-50) -----------------------------------------------------------
const HIDDEN_KEY = "jamora.cockpit.hidden";
export const readHidden = () => { const stored = readStored(HIDDEN_KEY, []); return Array.isArray(stored) ? stored.filter((id) => typeof id === "string") : []; };
export const writeHidden = (ids) => writeStored(HIDDEN_KEY, ids);
export const toggleHidden = (hidden, id) => (hidden.includes(id) ? hidden.filter((x) => x !== id) : [...hidden, id]);

export function cockpitError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail?.msg) return detail.msg;
  return fallback;
}
